<template>
  <v-dialog :value="value" max-width="980" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">Partners activos</v-card-title>
      <v-tabs v-model="tab" class="px-4">
        <v-tab v-for="partner in partners" :key="partner.id">{{ partner.partnerName }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text class="pt-4">
        <v-progress-linear v-if="loading" indeterminate class="mb-3" />
        <v-alert v-if="error" type="warning" text dense>{{ error }}</v-alert>
        <v-tabs-items v-model="tab">
          <v-tab-item v-for="partner in partners" :key="partner.id">
            <div class="d-flex flex-wrap align-center mb-4 partners__intro">
              <span class="text-body-2">{{ partner.audience }}</span>
              <v-spacer />
              <v-btn
                v-for="link in partner.decks"
                :key="link.url"
                :href="link.url"
                target="_blank"
                rel="noopener"
                x-small
                text
                color="primary"
              >
                <v-icon x-small left>mdi-open-in-new</v-icon>
                {{ link.label }}
              </v-btn>
            </div>

            <h4 class="text-subtitle-1 font-weight-bold mb-1">Planes y precios</h4>
            <div v-for="table in partner.priceTables" :key="table.title" class="mb-4">
              <div class="text-caption mb-1">
                {{ table.title }} · {{ table.unit }}. ↑ más caro y ↓ más barato que nuestra lista del mismo tramo;
                haz clic en un precio para ver la diferencia.
              </div>
              <v-simple-table dense class="partners__table">
                <thead>
                  <tr>
                    <th>Tramo</th>
                    <th v-for="column in table.columns" :key="column" class="text-right">{{ column }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in table.rows" :key="row.range">
                    <td>{{ row.range }}</td>
                    <td
                      v-for="(price, index) in row.prices"
                      :key="index"
                      class="text-right"
                      :class="{ 'partners__diff': priceArrow(price, ourPrice(table, row, index)) }"
                    >
                      <v-menu offset-y left :close-on-content-click="false">
                        <template #activator="{ on, attrs }">
                          <span class="partners__price" v-bind="attrs" v-on="on">
                            {{ money(price) }}
                            <span
                              v-if="priceArrow(price, ourPrice(table, row, index))"
                              class="partners__arrow"
                            >{{ priceArrow(price, ourPrice(table, row, index)) }}</span>
                          </span>
                        </template>
                        <v-card class="pa-3 text-body-2">
                          <div>Partner: <span class="font-weight-bold">{{ money(price) }}</span></div>
                          <div v-if="ourPrice(table, row, index)">
                            {{ table.flexible ? 'Pawer (bolsa)' : 'Pawer' }}: {{ money(ourPrice(table, row, index)) }}
                            · {{ gapText(price, ourPrice(table, row, index)) }}
                          </div>
                        </v-card>
                      </v-menu>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <div class="d-flex align-center flex-wrap mb-1 partners__intro">
              <h4 class="text-subtitle-1 font-weight-bold">Coberturas</h4>
              <v-spacer />
              <span class="text-caption"><span class="partners__swatch" /> Distinto a nuestro plan estándar</span>
            </div>
            <v-simple-table dense class="partners__table mb-4">
              <thead>
                <tr>
                  <th>
                    <div>Cobertura</div>
                    <div class="text-caption partners__muted">Eventos al año × tope por evento</div>
                  </th>
                  <th v-for="plan in partner.plans" :key="plan.name">
                    <div class="font-weight-bold">{{ plan.name }}</div>
                    <div class="text-caption">vs {{ planLabel(plan.comparesTo) }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Servicios digitales (app, televeterinaria, club, apadrinamiento)</td>
                  <td v-for="plan in partner.plans" :key="plan.name">Incluido</td>
                </tr>
                <tr v-for="row in rowsOf(partner)" :key="row.code">
                  <td>{{ serviceName(row.code) }}</td>
                  <td
                    v-for="(cell, index) in row.cells"
                    :key="index"
                    class="partners__cell"
                    :class="{ 'partners__diff': cell && cell.diff !== 'same' }"
                  >
                    <v-menu v-if="cell" offset-y left :close-on-content-click="false" :disabled="cell.diff === 'same'">
                      <template #activator="{ on, attrs }">
                        <span :class="{ 'partners__price': cell.diff !== 'same' }" v-bind="attrs" v-on="on">
                          {{ cell.partner ? coverageText(cell.partner) : 'No incluye' }}
                          <span v-if="arrowOf(cell)" class="partners__arrow">{{ arrowOf(cell) }}</span>
                        </span>
                      </template>
                      <v-card class="pa-3 text-body-2 font-weight-regular">
                        <div>Partner: <span class="font-weight-bold">{{ cell.partner ? coverageText(cell.partner) : 'No incluye' }}</span></div>
                        <div>{{ standardText(cell) }}</div>
                        <div v-for="line in differenceLines(cell)" :key="line">{{ line }}</div>
                      </v-card>
                    </v-menu>
                    <span v-else class="partners__none">—</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <h4 class="text-subtitle-1 font-weight-bold mb-1">Además</h4>
            <ul class="partners__list">
              <li v-for="(item, index) in partner.highlights" :key="index">{{ item }}</li>
            </ul>
            <p v-if="partner.notes" class="text-caption mt-3 mb-0">{{ partner.notes }}</p>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ActuarialService from '../services/actuarial.service';
import { changeOf, coverageRows, coverageShort, DIFF, listPriceAt, priceGap } from '../utils/partner-plans.utils';
import { formatMoney, PLAN_OPTIONS } from '../utils/b2b-quotes.utils';

/** Names of partner services outside our catalog. */
const EXTRA_SERVICE_NAMES = { TRASLADO: 'Traslado por accidente traumático' };

/**
 * Active partners: their plans, prices and coverages, each coverage marked
 * where it differs from the standard plan it is compared with.
 */
export default {
  name: 'PartnersModal',
  props: {
    value: { type: Boolean, default: false },
    /** Standard plans of the active parameters: [{ code, items }]. */
    standardPlans: { type: Array, default: () => [] },
    /** Reimbursable services of Chile: [{ code, name, cap }]. */
    services: { type: Array, default: () => [] },
    /** Our Chilean list tiers: [{ from, to, virtual, wellness, urgencias }]. */
    tiers: { type: Array, default: () => [] },
    /** Price factor of a Flexible pool. */
    flexibleFactor: { type: Number, default: 1.25 }
  },
  data() {
    return { tab: 0, partners: [], loading: false, error: '' };
  },
  watch: {
    value(open) {
      if (open && this.partners.length === 0) this.load();
    }
  },
  computed: {
    /** Standard plans with the catalog cap on every item, as the cotizador shows them. */
    standardWithCaps() {
      return this.standardPlans.map((plan) => ({
        code: plan.code,
        items: plan.items.map((item) => ({ ...item, cap: item.cap ?? this.catalogCap(item.serviceCode) }))
      }));
    }
  },
  methods: {
    /** Partner offers with margins from the snapshot the cotizador uses (or the latest if none is published). */
    async load() {
      this.loading = true;
      this.error = '';
      let res = await ActuarialService.partnerOffers('CL', 'published');
      if (res.status === 'ok' && !res.data.snapshotId) res = await ActuarialService.partnerOffers('CL', 'latest');
      this.loading = false;
      if (res.status === 'ok') this.partners = res.data.offers;
      else this.error = 'No se pudieron cargar los partners.';
    },
    catalogCap(code) {
      return this.services.find((s) => s.code === code)?.cap ?? null;
    },
    rowsOf(partner) {
      return coverageRows(partner.plans, this.standardWithCaps, this.services.map((s) => s.code));
    },
    serviceName(code) {
      return this.services.find((s) => s.code === code)?.name ?? EXTRA_SERVICE_NAMES[code] ?? code;
    },
    planLabel(code) {
      return PLAN_OPTIONS.find((p) => p.value === code)?.text ?? code;
    },
    ourPrice(table, row, index) {
      const plan = table.plans?.[index];
      if (!plan) return null;
      return listPriceAt(this.tiers, row.from, plan, table.flexible ? this.flexibleFactor : null);
    },
    /** ↑ the partner is dearer than our list, ↓ cheaper; nothing when equal (rounded to 1%). */
    priceArrow(price, ours) {
      const gap = priceGap(price, ours);
      if (gap === null || Math.round(gap * 100) === 0) return '';
      return gap > 0 ? '↑' : '↓';
    },
    gapText(price, ours) {
      const gap = priceGap(price, ours);
      const pct = Math.round(Math.abs(gap) * 100);
      if (pct === 0) return 'igual';
      return gap > 0 ? `+${pct}%` : `−${pct}%`;
    },
    money(amount) {
      return formatMoney(amount, 'CLP');
    },
    coverageText(coverage) {
      return coverageShort(coverage.events, this.money(coverage.cap));
    },
    standardText(cell) {
      if (cell.diff === DIFF.ADDED) return 'No está en el estándar';
      return `Estándar: ${this.coverageText({ events: cell.standard.eventsPerYear, cap: cell.standard.cap })}`;
    },
    /** What changed against the standard, in words: "Más eventos: sin límite vs 2 al año". */
    differenceLines(cell) {
      if (cell.diff !== DIFF.CHANGED) return [];
      const change = changeOf(cell.partner, cell.standard);
      const lines = [];
      if (change.events) {
        const ours = `${cell.standard.eventsPerYear} al año`;
        const theirs = cell.partner.events === null ? 'sin límite' : `${cell.partner.events} al año`;
        lines.push(`${change.events === 'up' ? 'Más' : 'Menos'} eventos: ${theirs} vs ${ours}`);
      }
      if (change.cap) {
        lines.push(`Tope ${change.cap === 'up' ? 'más alto' : 'más bajo'}: ${this.money(cell.partner.cap)} vs ${this.money(cell.standard.cap)}`);
      }
      return lines;
    },
    /** ↑ more than the standard, ↓ less, ↕ when events and cap go opposite ways. */
    arrowOf(cell) {
      if (cell.diff !== DIFF.CHANGED) return '';
      const moves = new Set(Object.values(changeOf(cell.partner, cell.standard)).filter(Boolean));
      if (moves.size === 2) return '↕';
      return moves.has('up') ? '↑' : '↓';
    }
  }
};
</script>

<style scoped>
.partners__intro {
  gap: 8px;
}
.partners__table {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
}
.partners__diff {
  background: var(--pw-primary-tint);
  font-weight: 600;
}
.partners__price {
  cursor: pointer;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.partners__cell {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.partners__arrow {
  margin-left: 4px;
  color: var(--pw-text-muted);
}
.partners__muted {
  color: var(--pw-text-muted);
}
.partners__table th:first-child,
.partners__table td:first-child {
  min-width: 160px;
  max-width: 200px;
  white-space: normal;
}
.partners__swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  vertical-align: middle;
  border-radius: 3px;
  background: var(--pw-primary-tint);
}
.partners__none {
  color: var(--pw-text-faint);
}
.partners__list li {
  margin-bottom: 4px;
}
</style>
